import { parse, stringify } from 'query-string';
import { History } from 'history';
import { User, ValidationError, Organization } from '../../gs-core/common/models';
import { Subject } from './Subject';
import { SubjectTypes } from './SubjectType';
import { DocStatus, DocStatuses } from './DocStatus';
import { Doc, MAX_PAGE_SIZE } from './Doc';
import { Sort, SortTypes } from './Sort';

export interface FilterParamsFull {
  ids?: number[];
  doc?: Doc;
  subject?: Subject;
  text?: string;
  title?: string;
  owningUnits?: Organization[];
  createdBy?: User;
  statuses?: DocStatus[];
  page?: number;
  firstPageOnly?: boolean;
  pageSize?: number;
  sort?: Sort;
  sortDir?: 'asc' | 'desc';
  createdBefore?: number;
}

export interface FilterParams {
  ids?: number[];
  docId?: number;
  subjectUserId?: number;
  subjectType?: string;
  text?: string;
  title?: string;
  owningUnitIds?: number[];
  createdById?: number;
  statuses?: string[];
  page?: number;
  firstPageOnly?: boolean;
  pageSize?: number;
  sort?: Sort;
  sortDir?: 'asc' | 'desc';
  createdBefore?: number;
  toQueryString: () => string;
  toDocListUrl: (api?: boolean) => string;
  expectResultStructureToChange: (other: FilterParams) => boolean;
  toString(): string;
}

interface FilterParamsProps {
  ids?: (string | number)[];
  docId?: string | number;
  subjectUserId?: (string | number);
  subjectType?: string;
  text?: string;
  title?: string;
  owningUnitId?: (string | number);
  owningUnitIds?: (string | number)[];
  createdById?: (string | number);
  statuses?: string[];
  page?: (string | number);
  firstPageOnly?: (string | boolean);
  pageSize?: (string | number);
  sort?: Sort;
  sortDir?: 'asc' | 'desc';
  createdBefore?: (string | number);
}

// The idea behind this model is that we could store it in the db if we wanted
export const FilterParamsModel = {
  create(props: FilterParamsProps | FilterParams): FilterParams {
    return {
      ids: parseIdArray(props.ids),
      docId: parseOptionalInteger(props.docId),
      subjectUserId: parseOptionalInteger(props.subjectUserId),
      subjectType: props.subjectType && SubjectTypes.parse(props.subjectType.toUpperCase()),
      text: props.text,
      title: props.title,
      owningUnitIds: parseIdArray(props.owningUnitIds || (props as FilterParamsProps).owningUnitId),
      createdById: parseOptionalInteger(props.createdById),
      statuses: parseStatuses(props.statuses),
      firstPageOnly: props.firstPageOnly ? true : undefined,
      page: parseOptionalInteger(props.page),
      pageSize: parseOptionalInteger(props.pageSize, MAX_PAGE_SIZE),
      sort: SortTypes.parse(props.sort),
      sortDir: SortTypes.parseDir(props.sortDir),
      createdBefore: parseOptionalInteger(props.createdBefore),
      toQueryString,
      toDocListUrl,
      expectResultStructureToChange,
      toString
    };
  },

  createFromFull(props: Partial<FilterParamsFull>): FilterParams {
    return {
      ids: parseIdArray(props.ids),
      docId: props.doc && props.doc.id,
      subjectUserId: props.subject && parseOptionalInteger(props.subject.user.id),
      subjectType: props.subject && SubjectTypes.parse(props.subject.type.toUpperCase()),
      text: props.text,
      title: props.title,
      owningUnitIds: parseIdArray((props.owningUnits || []).map(unit => unit.id as number)),
      createdById: props.createdBy && parseOptionalInteger(props.createdBy.id),
      statuses: parseStatuses(props.statuses),
      firstPageOnly: props.firstPageOnly ? true : undefined,
      page: parseOptionalInteger(props.page),
      sort: SortTypes.parse(props.sort),
      sortDir: SortTypes.parseDir(props.sortDir),
      pageSize: parseOptionalInteger(props.pageSize, MAX_PAGE_SIZE),
      createdBefore: parseOptionalInteger(props.createdBefore),
      toQueryString,
      toDocListUrl,
      expectResultStructureToChange,
      toString
    };
  },

  createFromQuery(queryParams: History.Search, defaults?: FilterParamsProps): FilterParams {
    return FilterParamsModel.create({ ...defaults, ...parse(queryParams) });
  }
};

export const FilterParamsFullModel = {
  create(props: Partial<FilterParamsFull>): FilterParamsFull {
    return {
      ids: parseIdArray(props.ids),
      doc: props.doc,
      subject: props.subject,
      text: props.text,
      title: props.title,
      owningUnits: props.owningUnits ? props.owningUnits.map(unit => unit) : undefined,
      createdBy: props.createdBy,
      statuses: parseStatuses(props.statuses),
      firstPageOnly: props.firstPageOnly ? true : undefined,
      page: parseOptionalInteger(props.page),
      sort: SortTypes.parse(props.sort),
      sortDir: SortTypes.parseDir(props.sortDir),
      pageSize: parseOptionalInteger(props.pageSize, MAX_PAGE_SIZE),
      createdBefore: parseOptionalInteger(props.createdBefore)
    };
  }
};

function toDocListUrl(this: FilterParams, api: boolean = false): string {
  const queryString = this.toQueryString();
  let url = '/';

  if (api) {
    url = '/api';
    if (this.subjectType && this.subjectUserId) {
      url += `/subject/${this.subjectType.toLowerCase()}/${this.subjectUserId}/doc/id`;
    } else if (this.owningUnitIds && this.owningUnitIds.length === 1) {
      url += `/unit/${this.owningUnitIds[0]}/doc/id`;
    } else {
      url += '/unit/doc/id';
    }
  } else {
    if (this.subjectType && this.subjectUserId) {
      url += `${this.subjectType.toLowerCase()}/${this.subjectUserId}`;
    } else if (this.owningUnitIds && this.owningUnitIds.length === 1) {
      url += `unit/${this.owningUnitIds[0]}`;
    } else if (!this.createdById) {
      url += 'unit';
    }
  }

  if (queryString.length) {
    url = `${url}?${queryString}`;
  }

  return url;
}

function toQueryString(this: FilterParams): string {
  return stringify({
    ids: this.ids,
    text: this.text,
    title: this.title,
    createdById: this.createdById,
    owningUnitIds: this.owningUnitIds && this.owningUnitIds.length ? this.owningUnitIds : undefined,
    statuses: this.statuses,
    firstPageOnly: this.firstPageOnly,
    page: this.page,
    pageSize: this.pageSize,
    sort: this.sort,
    sortDir: this.sortDir,
    createdBefore: this.createdBefore
  });
}

function toString(this: FilterParams): string {
  const builder = Object.keys(this)
    .filter(prop => !['page', 'firstPageOnly', 'pageSize', 'createdBefore'].includes(prop))
    .reduce(
      (props, prop) => {
        const value = this[prop];

        if (value && !(value instanceof Function) && (!Array.isArray(value) || value.length)) {
          props.push(`${prop}: ${value}`);
        }

        return props;
      },
      [] as string[]
    );

  return `Filter(${builder.join(', ')})`;
}

function expectResultStructureToChange(this: FilterParams, other: FilterParams): boolean {
  return (
    this.subjectUserId !== other.subjectUserId ||
    this.subjectType !== other.subjectType ||
    this.text !== other.text ||
    this.title !== other.title ||
    arraysDiff(this.owningUnitIds, other.owningUnitIds) ||
    this.createdById !== other.createdById ||
    arraysDiff(this.statuses, other.statuses) ||
    this.createdBefore !== other.createdBefore
  );
}

function arraysDiff(itemsA: (string | number)[] = [], itemsB: (string | number)[] = []): boolean {
  return itemsA.some(itemA => !itemsB.includes(itemA)) ||
    itemsB.some(itemB => !itemsA.includes(itemB));
}

function parseIdArray(values?: string | number | (number | string)[], max?: number): number[] | undefined {
  if (values) {
    values = Array.isArray(values) ? values : [values];

    if (values.length === 0) {
      return undefined;
    }

    if (values.length > MAX_PAGE_SIZE) {
      throw new Error(`Too many ids ${values.length}`);
    }

    const possibleIntegers = values.map((value: number | string) => parseOptionalInteger(value, max));
    const invalidValues = values.filter((possiblyInvalid, pos: number) => !possibleIntegers[pos]);

    if (invalidValues.length) {
      throw new Error(`Could not parse integers ${invalidValues}`);
    }

    return possibleIntegers as number[];
  }
  return undefined;
}

function parseOptionalInteger(value?: string | number, max?: number, min: number = 1): number | undefined {
  if (value) {
    const num = Number.parseInt(value as string, 10);
    if (Number.isNaN(num) || (max && (num > max || num < min))) {
      throw new ValidationError(`Invalid number param ${value}`);
    }
    return num;
  }
  return undefined;
}

function parseStatuses(values: string[] | string | undefined): DocStatus[] | undefined {
  if (values) {
    const statuses = (Array.isArray(values) ? values : [values])
      .map(status => DocStatuses.parse(status.toUpperCase()));
    const filterableStatuses = DocStatuses.filterable();
    const invalidStatuses = statuses.filter(status => !filterableStatuses.includes(status as DocStatus));
    if (invalidStatuses.length) {
      throw new ValidationError(`You are not allowed to filter by ${invalidStatuses.join(',')} statuses`);
    }
    return statuses;
  }
  return undefined;
}



// WEBPACK FOOTER //
// ./src/common/models/FilterParams.ts